import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./securityCompliance.css";

import iso9001 from "../../../assets/home/logo01.svg";
import iso27001 from "../../../assets/home/logo02.svg";
import tisax from "../../../assets/home/logo03.svg";
import iso30107 from "../../../assets/home/new-iso.png";
import gdpr from "../../../assets/home/gdpreu.png";
import hipaa from "../../../assets/home/logo04.svg";

gsap.registerPlugin(ScrollTrigger);

const certifications = [
  { name: "ISO/IEC 27001:2022", image: iso27001 },
  { name: "ISO 9001:2015", image: iso9001 },
  { name: "TISAX Level 3", image: tisax },
  { name: "ISO/IEC 30107-1:2016", image: iso30107 },
  { name: "GDPR", image: gdpr },
  { name: "HIPAA", image: hipaa },
];

export default function SecurityCompliance() {
  const sectionRef = useRef(null);

  useLayoutEffect(() => {
  const section = sectionRef.current;
  if (!section) return;

  const mm = gsap.matchMedia();

  mm.add("(prefers-reduced-motion: no-preference)", () => {
    const headingElements = [
      ".security-eyebrow",
      ".security-title",
      ".security-description",
    ]
      .map((s) => section.querySelector(s))
      .filter(Boolean);

    const certificationsRow = section.querySelector(".security-certifications");
    const certificationCards = gsap.utils.toArray(".security-cert", section);

    gsap.fromTo(
      headingElements,
      { autoAlpha: 0, y: 22 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: { trigger: section, start: "top 80%", once: true },
      }
    );

    if (certificationsRow && certificationCards.length) {
      gsap.fromTo(
        certificationCards,
        { autoAlpha: 0, y: 24 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.65,
          stagger: 0.16,
          ease: "power2.out",
          scrollTrigger: {
            trigger: certificationsRow,
            start: "top 88%",
            once: true,
          },
        }
      );
    }
  }, section);

  const refresh = () => ScrollTrigger.refresh();
  document.fonts?.ready.then(refresh);
  const refreshId = requestAnimationFrame(refresh);

  return () => {
    cancelAnimationFrame(refreshId);
    mm.revert();
  };
}, []);

  return (
    <section
      ref={sectionRef}
      className="security-section"
      id="security-compliance"
    >
      <div className="security-inner">
        <div className="security-heading-row">
          <div className="security-content">
            <span className="security-eyebrow">
              <span className="security-eyebrow-dot" />
              SECURITY &amp; COMPLIANCE
            </span>

            <h2 className="security-title">
              <span className="security-title-line">
                Certified to Handle Your
              </span>{" "}
              <br className="security-break" />
              Most <span className="security-title-accent">Sensitive Data</span>
            </h2>
          </div>

          <p className="security-description">
            100% in-house delivery. No outsourcing, no work-from-home access to
            client data, no exceptions.
          </p>
        </div>

        <div
          className="security-certifications"
          aria-label="Certifications and compliance standards"
        >
          {certifications.map((cert) => (
            <div className="security-cert" key={cert.name}>
              <div className="security-cert-image">
                <img
                  src={cert.image}
                  alt={cert.name}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <span className="security-cert-name">{cert.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
